import { isPartner } from "../../../helpers/commons.helpers";

/** Get the commission percentage based on the customer type and commercial terms.
 * @param {Object} customer - The customer object.
 * @returns {number} - The commission percentage (0 if not applicable).
 */
export function getCommissionByCustomer(customer) {
    if (isPartner(customer.type)) {
        let commercialTerms = customer.commercialTerms || customer.type_charge;
        return commercialTerms === "commission"
            ? customer.commission
            : 0;
    }
    return 0;
}

/**
 * Calculate the commission amount based on the gross price and commission percentage.
 * @param {*} grossPrice - The gross price of the reservation.
 * @param {*} commission - The commission percentage.
 * @returns {number} - The calculated commission amount.
 */
export function calculateCommissionAmount(grossPrice, commission) {
    let total = grossPrice * (commission / 100);
    return parseFloat(total.toFixed(2));
}

/** Calculate the total net profit from a reservation.
 * @param {number} grossPrice - The gross price of the reservation.
 * @param {number} netPrice - The net price of the reservation.
 * @param {number} platformAmount - The platform fee amount.
 * @param {number} paymentProcessorAmount - The payment processor fee amount.
 * @returns {number} - The total net profit.
 */
export function calculateTotalNetProfit(grossPrice, netPrice, platformAmount, paymentProcessorAmount) {
    let total = grossPrice - netPrice - platformAmount - paymentProcessorAmount;
    return parseFloat(total.toFixed(2));
}

/** Calculate the total net price from an array of net prices.
 * @param {Array} netPrices - An array of net prices.
 * @returns {number} - The total net price.
 */
export function calculateTotalNetPrice(netPrices = []) {
    let total = netPrices.reduce(
        (accumulator, currentValue) => accumulator + currentValue,
        0
    );
    return parseFloat(total.toFixed(2));
}

/** Calculate the total gross price from an array of gross prices.
 * @param {Array} grossPrices - An array of gross prices.
 * @returns {number} - The total gross price.
 */
export function calculateTotalGrossPrice(grossPrices = []) {
    let total = grossPrices.reduce(
        (accumulator, currentValue) => accumulator + currentValue,
        0
    );
    return parseFloat(total.toFixed(2));
}

/** Calculate the total net price of extras.
 * @param {Array} extras - An array of extra objects with net_price property.
 * @returns {number} - The total net price of extras.
 */
export function calculateTotalExtrasNetPrice(extras = []) {
    let totalNetPrices = extras.reduce(
        (accumulator, currentValue) => accumulator + currentValue.net_price,
        0
    );
    return parseFloat(totalNetPrices.toFixed(2));
}

/** Calculate the total gross price of extras.
 * @param {Array} extras - An array of extra objects with gross_price property.
 * @returns {number} - The total gross price of extras.
 */
export function calculateTotalExtraGrossPrice(extras = []) {
    let totalGrossPrices = extras.reduce(
        (accumulator, currentValue) => accumulator + currentValue.gross_price,
        0
    );
    return parseFloat(totalGrossPrices.toFixed(2));
}

/** Calculate the total discounts from an array of discount objects.
 * @param {Array} discounts - An array of discount objects with total property.
 * @returns {number} - The total discounts.
 */
export function calculateTotalDiscounts(discounts = []) {
    let totalDiscounts = discounts.reduce(
        (accumulator, currentValue) => accumulator + currentValue.total,
        0
    );
    return parseFloat(totalDiscounts.toFixed(2));
}

/**
 * Calculate the total price including surcharges.
 * @param {number} basePrice - The base price before surcharges.
 * @param {Array} surcharges - List of surcharge objects.
 * @param {string} applyIn - The context in which to apply surcharges.
 * @returns {number} - The total price after applying surcharges.
 */
export function calculateTotalPrice(basePrice, surcharges = [], applyIn) {
    let applicable = getApplicableSurcharges(applyIn, surcharges);
    let percentage = sumPercentageSurcharges(applicable);
    let totalAmount = sumFixedSurcharges(applicable);
    return (basePrice * (1 + percentage / 100)) + totalAmount;
}

/**
 * Get surcharges applicable in a specific context.
 * @param {string} applyIn - The context in which to filter surcharges.
 * @param {Array} surcharges - List of surcharge objects.
 * @returns {Array} - Filtered list of applicable surcharges.
 */
export function getApplicableSurcharges(applyIn, surcharges = []) {
    return surcharges.filter(surcharge => surcharge.apply_in === applyIn);
}

/**
 * Calculate the total percentage from percentage-based surcharges.
 * @param {Array} surcharges - List of surcharge objects.
 * @returns {number} - Total percentage from percentage-based surcharges.
 */
export function sumFixedSurcharges(surcharges = []) {
    return surcharges.reduce((total, surcharge) => {
        if (surcharge.type !== "percentage") {
            return total + surcharge.value;
        }
        return total;
    }, 0);
}

/**
 * Calculate the total percentage from percentage-based surcharges.
 * @param {Array} surcharges - List of surcharge objects.
 * @returns {number} - Total percentage from percentage-based surcharges.
 */
export function sumPercentageSurcharges(surcharges = []) {
    return surcharges.reduce((total, surcharge) => {
        if (surcharge.type === "percentage") {
            return total + surcharge.value;
        }
        return total;
    }, 0);
}

/**
 * Normalize a price amount to two decimal places.
 * @param {number} amount - The price amount to normalize.
 * @returns {number} - The normalized price amount.
 */
export function normalizePriceAmount(amount) {
    return parseFloat(amount.toFixed(2));
}

/** Format a price amount to a string with two decimal places.
 * @param {number} amount - The price amount to format.
 * @returns {string} - The formatted price amount as a string.
 */
export function formatPriceAmount(amount) {
    return amount.toFixed(2);
}

/**
 * Compares two price compositions to determine if they are the same.
 *
 * @param {Array} oldComposition - The old price composition array.
 * @param {Array} newComposition - The new price composition array.
 * @returns {boolean} - Returns true if the compositions are the same, false otherwise.
 */
export function isSamePriceComposition(oldComposition, newComposition) {
    const oldList = Array.isArray(oldComposition) ? oldComposition : [];
    const newList = Array.isArray(newComposition) ? newComposition : [];

    if (oldList.length !== newList.length) {
        return false;
    }

    for (const oldItem of oldList) {
        const match = newList.find(newItem => newItem.type === oldItem.type);
        if (!match) return false;
        if (oldItem.basePrice == null || match.basePrice == null) return false;
        const oldBasePrice = normalizePriceAmount(parseFloat(oldItem.basePrice));
        const newBasePrice = normalizePriceAmount(parseFloat(match.basePrice));
        if (oldBasePrice !== newBasePrice) return false;
    }

    return true;
}

/** * Compares two surcharge compositions to determine if they are the same.
 *
 * @param {Array} oldSurcharges - The old surcharge composition array.
 * @param {Array} newSurcharges - The new surcharge composition array.
 * @returns {boolean} - Returns true if the compositions are the same, false otherwise.
 */
export function isSameSurchargesComposition(oldSurcharges = [], newSurcharges = []) {
    if (oldSurcharges.length !== newSurcharges.length) {
        return false;
    }
    const oldFixedSurchargesTotal = sumFixedSurcharges(oldSurcharges);
    const newFixedSurchargesTotal = sumFixedSurcharges(newSurcharges);
    const oldSumFixedSurcharges = normalizePriceAmount(oldFixedSurchargesTotal);
    const newSumFixedSurcharges = normalizePriceAmount(newFixedSurchargesTotal);
    if (oldSumFixedSurcharges !== newSumFixedSurcharges) {
        return false;
    }

    const oldPercentageSurchargesTotal = sumPercentageSurcharges(oldSurcharges);
    const newPercentageSurchargesTotal = sumPercentageSurcharges(newSurcharges);
    const oldPercentageSurcharges = normalizePriceAmount(oldPercentageSurchargesTotal);
    const newPercentageSurcharges = normalizePriceAmount(newPercentageSurchargesTotal);
    if (oldPercentageSurcharges !== newPercentageSurcharges) {
        return false;
    }

    return true;
}